import { fetchConfig, requestIdleCallback } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Upsell slider of the cart drawer. cart-upsells.js
 *
 * Collection mode: cards are rendered in Liquid, this element only handles the "+" buttons.
 * Recommendations mode: fetches Shopify product recommendations for `data-product-id`
 * (complementary first, topped up with related), caches them per product, hides the
 * products listed in `data-exclude` and shows `data-max` cards at most. The drawer morph
 * keeps the injected cards (data-skip-subtree-update) and only syncs the data attributes.
 */

const SECTION_ID = 'cart-upsells';
const CACHE_TTL = 30 * 60 * 1000;
const ERROR_DURATION = 4000;

/** @typedef {{ id: string, html: string }} UpsellCard */

/** @type {Map<string, Promise<UpsellCard[]>>} */
const memoryCache = new Map();

/**
 * @param {string} productId
 * @returns {string}
 */
function cacheKey(productId) {
  const currency = window.Shopify?.currency?.active ?? '';
  const locale = window.Shopify?.locale ?? '';
  return `cart-upsells:v1:${locale}:${currency}:${productId}`;
}

/**
 * @param {string} key
 * @returns {UpsellCard[] | null}
 */
function readSession(key) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(key) ?? 'null');
    if (stored && Date.now() - stored.timestamp < CACHE_TTL && Array.isArray(stored.cards)) return stored.cards;
  } catch {}
  return null;
}

/**
 * @param {string} key
 * @param {UpsellCard[]} cards
 */
function writeSession(key, cards) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), cards }));
  } catch {}
}

/**
 * Fetches the recommendations section and extracts the cards.
 * @param {string} baseUrl
 * @param {string} productId
 * @param {'complementary' | 'related'} intent
 * @returns {Promise<UpsellCard[]>}
 */
async function fetchCards(baseUrl, productId, intent) {
  const params = new URLSearchParams({ product_id: productId, limit: '10', intent, section_id: SECTION_ID });
  const response = await fetch(`${baseUrl}?${params}`);
  if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  return Array.from(doc.querySelectorAll('[data-cart-upsell-card]'), (card) => ({
    id: card.getAttribute('data-product-id') ?? '',
    html: card.outerHTML,
  }));
}

/**
 * Complementary products first, topped up with related ones. Cached per product.
 * @param {string} baseUrl
 * @param {string} productId
 * @param {number} max
 * @returns {Promise<UpsellCard[]>}
 */
function getRecommendations(baseUrl, productId, max) {
  const key = cacheKey(productId);
  const cached = memoryCache.get(key);
  if (cached) return cached;

  const stored = readSession(key);
  if (stored) {
    const promise = Promise.resolve(stored);
    memoryCache.set(key, promise);
    return promise;
  }

  const promise = (async () => {
    const results = await Promise.allSettled([fetchCards(baseUrl, productId, 'complementary')]);
    /** @type {UpsellCard[]} */
    let cards = results[0].status === 'fulfilled' ? results[0].value : [];

    if (cards.length < max) {
      const related = await fetchCards(baseUrl, productId, 'related').catch(() => null);
      if (!related && results[0].status === 'rejected') throw new Error('Recommendations unavailable');

      const seen = new Set(cards.map((card) => card.id));
      cards = cards.concat((related ?? []).filter((card) => !seen.has(card.id)));
    }

    writeSession(key, cards);
    return cards;
  })();

  memoryCache.set(key, promise);
  promise.catch(() => memoryCache.delete(key));
  return promise;
}

/**
 * Reads the cart item count from the re-rendered cart sections.
 * @param {Record<string, string> | undefined} sections
 * @param {string[]} sectionIds
 * @returns {number | null}
 */
function readItemCount(sections, sectionIds) {
  for (const id of sectionIds) {
    const html = sections?.[id];
    if (!html) continue;

    const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
    if (count) return parseInt(count, 10);
  }
  return null;
}

class CartUpsells extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude'];

  /** @type {UpsellCard[]} */
  #cards = [];
  #renderedKey = '';
  #requestId = 0;
  #ready = false;
  /** @type {number | undefined} */
  #errorTimeout;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#ready = true;

    if (!this.#isRecommendations) return;

    const productId = this.dataset.productId;
    const stored = productId ? readSession(cacheKey(productId)) : null;

    if (stored) {
      this.#cards = stored;
      this.#render();
    }

    requestIdleCallback(() => this.#load(), { timeout: 1500 });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#errorTimeout);
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#ready || oldValue === newValue || !this.#isRecommendations) return;

    if (name === 'data-product-id') {
      this.#load();
    } else {
      this.#render();
    }
  }

  get #isRecommendations() {
    return this.dataset.mode === 'recommendations';
  }

  get #max() {
    return Number(this.dataset.max) || 5;
  }

  async #load() {
    const { productId, url } = this.dataset;
    const requestId = ++this.#requestId;

    if (!productId || !url) {
      this.#cards = [];
      this.#render();
      return;
    }

    try {
      const cards = await getRecommendations(url, productId, this.#max);
      if (requestId !== this.#requestId) return;

      this.#cards = cards;
      this.#render();
    } catch (error) {
      if (requestId !== this.#requestId) return;
      console.error(error);
      this.#cards = [];
      this.#render();
    }
  }

  /**
   * Shows the cached cards minus the excluded products. Skips the DOM write when nothing changed
   * so images and scroll position stay put across morphs.
   */
  #render() {
    const inner = this.querySelector('.cart-upsells__inner');
    const track = this.querySelector('.cart-upsells__track');
    if (!(inner instanceof HTMLElement) || !track) return;

    const excluded = new Set((this.dataset.exclude ?? '').split(',').filter(Boolean));
    const visible = this.#cards.filter((card) => card.id && !excluded.has(card.id)).slice(0, this.#max);
    const key = visible.map((card) => card.id).join(',');

    if (key !== this.#renderedKey) {
      this.#renderedKey = key;
      track.innerHTML = visible.map((card) => card.html).join('');
      track.scrollLeft = 0;
    }

    inner.hidden = visible.length === 0;
  }

  /**
   * Adds the first available variant of the clicked card to the cart.
   * @param {MouseEvent} event
   */
  #onClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const productId = button.closest('[data-product-id]')?.getAttribute('data-product-id') ?? undefined;
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'), (element) =>
      element instanceof HTMLElement ? element.dataset.sectionId : undefined
    ).filter((id) => typeof id === 'string' && id.length > 0);

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#showError('');

    try {
      const response = await fetch(
        window.Theme?.routes?.cart_add_url ?? '/cart/add.js',
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Ajout impossible pour le moment.');
      }

      const itemCount = readItemCount(data.sections, /** @type {string[]} */ (sectionIds));

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          // Without a total count, let the cart icon increment its own count.
          source: itemCount === null ? 'product-form-component' : 'cart-upsells',
          itemCount: itemCount ?? 1,
          productId,
          variantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#showError(error instanceof Error ? error.message : 'Ajout impossible pour le moment.');
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * @param {string} message
   */
  #showError(message) {
    const errorElement = this.querySelector('.cart-upsells__error');
    if (!(errorElement instanceof HTMLElement)) return;

    clearTimeout(this.#errorTimeout);
    errorElement.textContent = message;
    errorElement.hidden = !message;

    if (message) {
      this.#errorTimeout = window.setTimeout(() => {
        errorElement.textContent = '';
        errorElement.hidden = true;
      }, ERROR_DURATION);
    }
  }
}

if (!customElements.get('cart-upsells')) {
  customElements.define('cart-upsells', CartUpsells);
}
